import DeleteIcon from '@mui/icons-material/Delete'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import KNDataTableMenu from 'components/KNDataTable/KNDataTableMenu'
import KNDataTableOverflow from 'components/KNDataTable/KNDataTableOverflow'
import type { KNDataTableColumn } from 'components/KNDataTable/types'
import KNConfirmationDialog from 'components/KNDialog/KNConfirmationDialog'
import { KNAcceptanceCircleIcon, KNCheckIcon, KNCrossCircleIcon } from 'components/KNIcon/KNIcon'
import { Role } from 'context/authentication/Role.types'
import { UserContext } from 'context/authentication/UserContext'
import { useTripsView } from 'context/TripsViewContext'
import { hasAccess } from 'global/helpers/authorization'
import { isSupportUser } from 'global/helpers/environment'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import QuickStatusUpdateDialog from 'screens/StatusManager/QuickStatusUpdateDialog'
import DeleteTripDialog from './DeleteTripDialog'
import DriverAssignment from './DriverAssignment'
import ShareDialog from './ShareDialog'
import { getTripAcceptanceColor, hasPendingTripAcceptance } from './TripAcceptance.helpers'
import TripCargo from './TripCargo'
import { TRIPS_QUERY_KEY, tripShareTokenQueryOptions } from './TripDashboard.service'
import type { TripData } from './TripDashboard.types'
import TripDates from './TripDates'
import TripHeader from './TripHeader'
import TripStatusChip from './TripStatusChip'
import VehicleAssignment from './VehicleAssignment'

interface TripTableProps {
  trips: TripData[]
  onChange?: (updatedTrip: TripData, action?: string) => void
  tripAcceptance: boolean
}

const TripTable = ({
  trips,
  onChange,
  tripAcceptance,
}: TripTableProps): ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [filteredTrips, setFilteredTrips] = useState<TripData[]>(trips)
  const [activeTrip, setActiveTrip] = useState<TripData>()
  const { filters, saveScrollPosition } = useTripsView()
  const { user } = useContext(UserContext)
  const isFetching = useIsFetching({ queryKey: [TRIPS_QUERY_KEY] })
  const quickStatusUpdateDialog = useDialog<typeof QuickStatusUpdateDialog>({
    onAction: (updatedTrip, action) => onChange?.(updatedTrip, action),
  })
  const shareDialog = useDialog<typeof ShareDialog>()
  const deleteTripDialog = useDialog<typeof DeleteTripDialog>({
    onAction: (updatedTrip, action) => onChange?.(updatedTrip, action),
  })
  const acceptConfirmationDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: () => {
      if (activeTrip) {
        onChange?.(activeTrip, 'trip_accepted')
      }
    },
  })
  const declineConfirmationDialog = useDialog<typeof KNConfirmationDialog>({
    onAction: () => {
      if (activeTrip) {
        onChange?.(activeTrip, 'trip_declined')
      }
    },
  })

  useEffect(() => {
    setFilteredTrips(tripAcceptance ? trips.filter((trip) => trip.tripAcceptanceStatus !== 'DECLINED') : trips)
  }, [trips, tripAcceptance])

  const handleLinkClick = useCallback((row: TripData) => {
    setActiveTrip(row)
    saveScrollPosition()
  }, [])

  const handleQuickStatusUpdateClick = useCallback((row: TripData) => {
    setActiveTrip(row)
    saveScrollPosition()
    quickStatusUpdateDialog.handleOpen()
  }, [])

  const handleShareClick = useCallback((row: TripData) => {
    setActiveTrip(row)
    shareDialog.handleOpen()
  }, [])

  const handleCopyClick = useCallback(async (row: TripData): Promise<void> => {
    const { weblinkToken } = await queryClient.fetchQuery(tripShareTokenQueryOptions(row.entityId))
    await navigator.clipboard.writeText(`${window.location.origin}/wl/${weblinkToken}`)
  }, [queryClient])

  const handleDeleteClick = useCallback((row: TripData) => {
    setActiveTrip(row)
    deleteTripDialog.handleOpen()
  }, [])

  const handleAcceptConfirmationDialogClick = useCallback((row: TripData) => {
    setActiveTrip(row)
    acceptConfirmationDialog.handleOpen()
  }, [])

  const handleDeclineConfirmationDialogClick = useCallback((row: TripData) => {
    setActiveTrip(row)
    declineConfirmationDialog.handleOpen()
  }, [])

  const tripColumnValue = (row: TripData): ReactElement => (
    <Stack spacing={0.25}>
      <TripHeader
        trip={row}
        linked
        withStatus={false}
        onLinkClick={handleLinkClick}
        highlights={filters.keywords}
      />
      <TripCargo trip={row} />
    </Stack>
  )

  const acceptanceColumnValue = (row: TripData): ReactElement | null => {
    const color = getTripAcceptanceColor(row.tripAcceptanceStatus)
    switch (row.tripAcceptanceStatus) {
      case 'ACCEPTED':
        return <KNCheckIcon sx={{ color }} />
      case 'DECLINED':
        return <KNCrossCircleIcon sx={{ color }} />
      case 'PENDING':
        return <KNAcceptanceCircleIcon sx={{ color }} />
      default:
        return null
    }
  }

  const statusColumnValue = (row: TripData): ReactElement => (
    <TripStatusChip status={row.status} />
  )

  const shipmentsColumnValue = (row: TripData): ReactElement => (
    <KNDataTableOverflow
      row={row}
      label={t('general.view_all')}
      header={t('screens.cs.trip_dashboard.card.columns.shipments')}
      items={row.shipmentNumbers}
      rightAligned
      highlights={filters.keywords}
    />
  )

  const driverVehicleColumnValue = (row: TripData): ReactElement => (
    <Grid container spacing={1} onClick={(event) => {
      event.stopPropagation()
    }}>
      <Grid size={{ md: 12, xl: 6 }}>
        <DriverAssignment trip={row} onChange={onChange} />
      </Grid>
      <Grid size={{ md: 12, xl: 6 }}>
        <VehicleAssignment trip={row} onChange={onChange} />
      </Grid>
    </Grid>
  )

  const firstPickupLastDeliveryColumnValue = (row: TripData): ReactElement => (
    <TripDates trip={row} keywords={filters.keywords} />
  )

  const actionsColumnValue = (row: TripData) => (
    <KNDataTableMenu
      label={t('general.more_options')}
      row={row}
      actions={[
        ...(hasPendingTripAcceptance(row) ? [
          {
            name: 'accept_trip',
            label: t('screens.cs.trip_details.accept_trip.accept'),
            onClick: handleAcceptConfirmationDialogClick,
          }, {
            name: 'decline_trip',
            label: t('screens.cs.trip_details.accept_trip.decline'),
            onClick: handleDeclineConfirmationDialogClick,
            bottomBorder: true,
          },
        ] : []),
        {
          name: 'copy',
          label: t('screens.cs.trip_dashboard.card.actions.copy_trip'),
          onClick: handleCopyClick,
        },
        {
          name: 'share',
          label: t('screens.cs.trip_dashboard.card.actions.share_trip'),
          onClick: handleShareClick,
        },
        ...(isSupportUser(user) ? [
          {
            name: 'delete',
            label: t('screens.cs.trip_dashboard.card.actions.delete'),
            icon: <DeleteIcon />,
            onClick: handleDeleteClick,
          },
        ] : []),
      ]}
    />
  )

  const columns = useMemo((): KNDataTableColumn<TripData>[] => [
    {
      name: 'trip',
      label: t('screens.cs.trip_dashboard.card.columns.trip'),
      getValue: tripColumnValue,
      sx: { width: { xs: '24rem', xxl: 'auto' } },
    },
    ...(tripAcceptance ? [{
      name: 'acceptance',
      label: t('screens.cs.trip_details.accept_trip.accept'),
      getValue: acceptanceColumnValue,
      sx: { width: '4rem' },
    }] : []),
    {
      name: 'status',
      label: t('screens.cs.trip_dashboard.card.columns.status'),
      getValue: statusColumnValue,
      sx: { width: '8rem' },
    },
    {
      name: 'shipments',
      label: t('screens.cs.trip_dashboard.card.columns.shipments'),
      getValue: shipmentsColumnValue,
      sx: { width: '12rem' },
    },
    {
      name: 'driver_vehicle',
      label: t('screens.cs.trip_dashboard.card.columns.driver_vehicle'),
      getValue: driverVehicleColumnValue,
      sx: { width: { xs: '12rem', xl: '18rem', xxl: '22rem' } },
    },
    {
      name: 'first_pickup_last_delivery',
      label: t('screens.cs.trip_dashboard.card.columns.first_pickup_last_delivery'),
      getValue: firstPickupLastDeliveryColumnValue,
      sx: { width: { xs: '15rem', xxl: '20rem' } },
    },
    ...hasAccess(Role.Editor) ? [{
      name: 'actions',
      label: '',
      getValue: actionsColumnValue,
      sx: { width: '2rem' },
    }] : [],
  ], [filters.keywords])

  return (
    <>
      <KNDataTable
        columns={columns}
        data={filteredTrips}
        onRowClick={handleQuickStatusUpdateClick}
        isLoading={isFetching > 0}
        sx={{
          marginX: -2,
          height: '100%',
        }}
        tableSx={{
          minWidth: '68rem',
        }}
      />
      {activeTrip && (
        <>
          <QuickStatusUpdateDialog
            payload={{
              trip: activeTrip,
            }}
            open={quickStatusUpdateDialog.isOpen}
            onAction={quickStatusUpdateDialog.handleAction}
            onClose={quickStatusUpdateDialog.handleClose}
          />
          <ShareDialog
            payload={{
              trip: activeTrip,
            }}
            open={shareDialog.isOpen}
            onAction={shareDialog.handleAction}
            onClose={shareDialog.handleClose}
          />
          <DeleteTripDialog
            payload={{
              trip: activeTrip,
            }}
            open={deleteTripDialog.isOpen}
            onAction={deleteTripDialog.handleAction}
            onClose={deleteTripDialog.handleClose}
          />
          <KNConfirmationDialog
            message={t('screens.cs.trip_details.accept_trip.trip_accept_confirmation')}
            continueLabel={t('screens.cs.trip_details.accept_trip.accept')}
            open={acceptConfirmationDialog.isOpen}
            onAction={acceptConfirmationDialog.handleAction}
            onClose={acceptConfirmationDialog.handleClose}
          />
          <KNConfirmationDialog
            message={t('screens.cs.trip_details.accept_trip.trip_decline_confirmation')}
            continueLabel={t('screens.cs.trip_details.accept_trip.decline')}
            open={declineConfirmationDialog.isOpen}
            onAction={declineConfirmationDialog.handleAction}
            onClose={declineConfirmationDialog.handleClose}
          />
        </>
      )}
    </>
  )
}

export default TripTable
